import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Download, Tags } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Link, useNavigate } from 'react-router-dom'
import {
  useImportableDatasets,
  useImportKnowledgeBase,
  ImportableDataset,
} from '@/hooks/useKnowledgeBases'
import { DataTable } from '@/components/common/DataTable'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function KnowledgeBaseImport() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useImportableDatasets({ page, pageSize, search })
  const importMutation = useImportKnowledgeBase()
  const { toast } = useToast()
  const navigate = useNavigate()

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedDataset, setSelectedDataset] = useState<ImportableDataset | null>(null)
  const [importName, setImportName] = useState('')

  const handleImportClick = (dataset: ImportableDataset) => {
    setSelectedDataset(dataset)
    setImportName(dataset.name)
    setImportDialogOpen(true)
  }

  const handleImportConfirm = () => {
    if (!selectedDataset) return

    importMutation.mutate(
      {
        ragflow_dataset_id: selectedDataset.id,
        name: importName.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          const tagsetCount = res.imported_tagsets?.length || 0
          const docCount = res.knowledge_base?.document_count || 0
          let msg = `Imported "${res.knowledge_base.name}" with ${docCount} document(s)`
          if (tagsetCount > 0) {
            msg += ` and ${tagsetCount} tagset(s)`
          }
          toast({ title: 'Import successful', description: msg })
          setImportDialogOpen(false)
          setSelectedDataset(null)
          navigate(`/app/knowledge/${res.knowledge_base.id}`)
        },
        onError: (err: any) => {
          const message = err?.response?.data?.error || err.message || 'Import failed'
          toast({ title: 'Import failed', description: message, variant: 'destructive' })
        },
      }
    )
  }

  const columns: ColumnDef<ImportableDataset, any>[] = [
    {
      accessorKey: 'name',
      header: 'Dataset Name',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-muted-foreground line-clamp-1 max-w-[250px]">
          {row.original.description || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'chunk_method',
      header: 'Chunk Method',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.chunk_method}</Badge>
      ),
    },
    {
      accessorKey: 'document_count',
      header: 'Docs',
      cell: ({ row }) => row.original.document_count,
    },
    {
      accessorKey: 'linked_tagset_count',
      header: 'Tagsets',
      cell: ({ row }) => {
        const count = row.original.linked_tagset_count
        if (count === 0) return <span className="text-muted-foreground">—</span>
        return (
          <div className="flex items-center gap-1">
            <Tags className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{count}</span>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          onClick={() => handleImportClick(row.original)}
          disabled={importMutation.isPending}
        >
          <Download className="h-4 w-4 mr-1" />
          Import
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/app/knowledge">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">Import from RAGflow</h1>
          </div>
          <p className="text-muted-foreground ml-12">
            Import existing RAGflow datasets as knowledge bases. Linked tagsets will be imported automatically.
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        getRowId={(row) => row.id}
        emptyState={
          <div className="text-center py-12">
            <Download className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No datasets to import</h3>
            <p className="text-muted-foreground mt-2">
              All RAGflow datasets are already imported, or there are none available.
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/app/knowledge">Back to Knowledge Bases</Link>
            </Button>
          </div>
        }
      />

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Knowledge Base</DialogTitle>
            <DialogDescription>
              Import this RAGflow dataset into Chatterpillar. You can optionally rename it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="import-name">Knowledge Base Name</Label>
              <Input
                id="import-name"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="Enter a name for the knowledge base"
              />
            </div>

            {selectedDataset && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <div>
                  <span className="font-medium">RAGflow ID:</span>{' '}
                  <code className="text-xs">{selectedDataset.id}</code>
                </div>
                <div>
                  <span className="font-medium">Chunk Method:</span>{' '}
                  {selectedDataset.chunk_method}
                </div>
                <div>
                  <span className="font-medium">Documents:</span>{' '}
                  {selectedDataset.document_count}
                </div>
                {selectedDataset.linked_tagset_count > 0 && (
                  <div>
                    <span className="font-medium">Linked Tagsets:</span>{' '}
                    {selectedDataset.linked_tagset_count} (will be auto-imported)
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={importMutation.isPending || !importName.trim()}
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
